import React from "react";
import ServiceFactory from "../../service";
import { RANGES, rangeLabelFor } from "../sales/territory-common";
import { rangeToDates } from "../../functions/range-window";
import account from "../../data/account";

// Store Performance (STORE_PERFORMANCE.md §1, mockups store-performance.html
// + store-targets.html). Rail: AnalyticsService /v1/store/* (Tenant policy —
// the service layer sends the bearer + x-app-id).

export function fetchStore(api) {
  const Service = ServiceFactory.getType(api, "product");
  return new Service().getAsync();
}
export function putStore(api, body) {
  const Service = ServiceFactory.getType(api, "product");
  return new Service().putAsync(body);
}

// Category names arrive HTML-encoded from the rollup ("Furniture &gt; Sofas").
export function decodeEntities(s) {
  return String(s || "").replace(/&gt;/g, ">").replace(/&lt;/g, "<").replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

// Trailing segment reads better in tight tables ("Furniture > Tables >
// Accent Tables > Coffee Tables" → "Coffee Tables"); full path in title=.
export function categoryLeaf(s) {
  const parts = decodeEntities(s).split(">").map(p => p.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "(uncategorized)";
}

// Attainment semantics (§3.7): a COMPLETED window's attainment is true
// actual-vs-plan — lead with it. An IN-PROGRESS window's attainment is "% of
// the full plan banked so far" (naturally <100 mid-period) — lead with PACE
// and label attainment "of plan".
export function isInProgressRange(range) {
  if (/^this_/.test(range) || range === "today") return true;
  if (/^custom_\d{8}_(\d{8})$/.test(range)) {
    const end = String(range).slice(-8);
    const today = new Date();
    const iso = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
    return end >= iso;
  }
  return false;
}
export function isAdminUser() {
  const groups = ((account.get() || {}).groups || []).map(g => String(g).toLowerCase());
  return ["owner", "administrator", "root"].some(g => groups.includes(g));
}
export function pct(v) {
  const n = Number(v);
  return Number.isFinite(n) ? `${n.toFixed(1)}%` : "—";
}

// ---------------------------------------------------------------- Stores redesign (2026-10-09)
// PlunkDocs mockups/stores-redesign-2026-10-09.html. Styles: sass/v2/_stores.scss (st-).

const n0 = v => Number(v) || 0;
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Rounded money: $3.8M, $1.82M, $775K, $42K, $9.8K, $812. */
export function stMoney(v) {
  const n = n0(v);
  if (!n) return "$0";
  const a = Math.abs(n);
  const s = n < 0 ? "-$" : "$";
  const trim = (x, d) => x.toFixed(d).replace(/\.?0+$/, "");
  if (a >= 1e9) return s + trim(a / 1e9, 1) + "B";
  if (a >= 999500) return s + trim(a / 1e6, 2) + "M";
  if (a >= 9950) return s + Math.round(a / 1e3) + "K";
  if (a >= 1e3) return s + trim(a / 1e3, 1) + "K";
  return s + Math.round(a);
}
/** Whole dollars: $2,894. */
export function stDollars(v) {
  const n = Math.round(n0(v));
  return (n < 0 ? "-$" : "$") + Math.abs(n).toLocaleString("en-US");
}
export function stCount(v) {
  return Math.round(n0(v)).toLocaleString("en-US");
}
/** Whole percent: 34%. Null-safe ("-"). */
export function stPct(v) {
  if (v == null || !Number.isFinite(Number(v))) return "-";
  return `${Math.round(Number(v))}%`;
}

/** Change against the comparison window: { text: "+12%", dir } or null when there is no prior. */
export function stChange(cur, prev) {
  const c = n0(cur),
    p = n0(prev);
  if (p <= 0) return null;
  const pct = (c - p) / p * 100;
  const r = Math.round(pct);
  return {
    dir: r > 0 ? "up" : r < 0 ? "down" : "flat",
    text: `${r > 0 ? "+" : r < 0 ? "-" : ""}${Math.abs(r)}%`
  };
}

/** A store known only by its numeric feed id ("29") reads "Outlet 29". The backend does this too (1.0.174). */
export function storeName(s) {
  const id = String(s && s.storeId || "").trim();
  const name = String(s && s.name || "").trim();
  if (name && name.toLowerCase() !== id.toLowerCase()) return name;
  if (/^\d+$/.test(id)) return `${s && s.type === "outlet" ? "Outlet" : "Store"} ${id}`;
  return name || id;
}
export const typeLabel = t => t === "outlet" ? "Outlet" : "Store";

/** "Sofas" and "SOFA" are one category. Key = leaf, lower case, last word singular. */
export function categoryKey(raw) {
  const words = categoryLeaf(raw).toLowerCase().replace(/\s+/g, " ").trim().split(" ");
  let w = words[words.length - 1];
  if (w.length > 4 && /ies$/.test(w)) w = w.slice(0, -3) + "y";else if (w.length > 4 && /(sses|shes|ches|xes|zes)$/.test(w)) w = w.slice(0, -2);else if (w.length > 3 && /s$/.test(w) && !/(ss|us|is)$/.test(w)) w = w.slice(0, -1);
  words[words.length - 1] = w;
  return words.join(" ");
}
/** Normal-case display: "LAMPS" → "Lamps"; mixed case stays as written. */
export function categoryName(raw) {
  const leaf = categoryLeaf(raw);
  if (leaf === "(uncategorized)") return "Uncategorized";
  if (/[A-Za-z]/.test(leaf) && leaf === leaf.toUpperCase()) {
    const l = leaf.toLowerCase();
    return l.charAt(0).toUpperCase() + l.slice(1);
  }
  return leaf;
}

/** "This month, Oct 1 to 9" / "This year, Jan 1 to Oct 9" / "Last year, Jan 1 to Dec 31, 2025". */
export function stRangeLabel(range, start, end) {
  const base = rangeLabelFor(range, RANGES);
  const parse = v => {
    const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
  };
  let a = parse(start),
    b = parse(end);
  if (!a || !b) {
    const pair = rangeToDates(range);
    if (!pair) return base;
    [a, b] = pair;
  }
  if (/^custom_/.test(String(range))) return base;
  const label = base.charAt(0) + base.slice(1).toLowerCase();
  const thisYear = new Date().getFullYear();
  const yr = b.getFullYear() !== thisYear ? `, ${b.getFullYear()}` : "";
  let dates;
  if (a.getTime() === b.getTime()) dates = `${MON[a.getMonth()]} ${a.getDate()}${yr}`;else if (a.getFullYear() !== b.getFullYear()) dates = `${MON[a.getMonth()]} ${a.getDate()}, ${a.getFullYear()} to ${MON[b.getMonth()]} ${b.getDate()}, ${b.getFullYear()}`;else if (a.getMonth() === b.getMonth()) dates = `${MON[a.getMonth()]} ${a.getDate()} to ${b.getDate()}${yr}`;else dates = `${MON[a.getMonth()]} ${a.getDate()} to ${MON[b.getMonth()]} ${b.getDate()}${yr}`;
  return `${label}, ${dates}`;
}

/** "this month's goal" / "last year's goal" / "the goal for these dates". */
export function goalPhrase(range) {
  const m = String(range || "").match(/^(this|last)_(week|month|quarter|year)$/);
  if (m) return `${m[1]} ${m[2]}'s goal`;
  if (range === "today") return "today's goal";
  return "the goal for these dates";
}
/** "month" for this_month etc.; "period" otherwise. */
export function periodWord(range) {
  const m = String(range || "").match(/_(week|month|quarter|year)$/);
  return m ? m[1] : "period";
}
/** "the same days last month" (backend label when served). */
export function compareLabel(data, range) {
  if (range === "all_time") return null; // nothing earlier to compare with
  if (data && data.compareLabel) return data.compareLabel;
  return isInProgressRange(range) ? `the same days last ${periodWord(range)}` : `the ${periodWord(range)} before`;
}

// ---- new reads (AnalyticsService /v1/store, 1.0.174) ----
export function fetchStoreTrend(range) {
  return fetchStore(`/store/performance/${range}?$groupBy=trend`);
}
export function fetchStoreProducts(range, storeId, top = 10) {
  return fetchStore(`/store/performance/${range}/${encodeURIComponent(storeId)}/products?top=${top}`);
}
export function fetchStoreCustomers(range, storeId, top = 10) {
  return fetchStore(`/store/performance/${range}/${encodeURIComponent(storeId)}/customers?top=${top}`);
}

/** Tiny 30-day sparkline (inline SVG), quiet placeholder when there is nothing to draw. */
export function MiniSpark({
  values
}) {
  const v = (values || []).map(x => Math.max(0, n0(x)));
  const mx = Math.max(0, ...v);
  if (v.length < 2 || !mx) return /*#__PURE__*/React.createElement("span", {
    className: "st-spark empty",
    "aria-hidden": "true"
  });
  const W = 120,
    H = 24;
  const pts = v.map((x, i) => `${(i / (v.length - 1) * W).toFixed(1)},${(H - 2 - x / mx * (H - 4)).toFixed(1)}`).join(" ");
  return /*#__PURE__*/React.createElement("svg", {
    className: "st-spark",
    viewBox: `0 0 ${W} ${H}`,
    preserveAspectRatio: "none",
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement("polyline", {
    points: `0,${H} ${pts} ${W},${H}`,
    className: "a"
  }), /*#__PURE__*/React.createElement("polyline", {
    points: pts,
    className: "l"
  }));
}

/** Progress bar to a goal (capped at 100% width). */
export function GoalBar({
  pct,
  big
}) {
  const w = Math.max(0, Math.min(100, n0(pct)));
  return /*#__PURE__*/React.createElement("span", {
    className: `st-bar${big ? " big" : ""}`
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      width: `${w}%`
    }
  }));
}
export function Change({
  c
}) {
  return c ? /*#__PURE__*/React.createElement("span", {
    className: `st-chg ${c.dir}`
  }, c.text) : null;
}